import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import i18n from "i18n"
import { FiInfo } from "react-icons/fi"

import useT from "utils/hooks/useT"
import Loading from "components/Loading"
import { Popup, ConfigContainer } from "components"
import { Container, Title, Text, Select } from "components/Form"
import SpecEditor from "components/Lims/SpecEditor"
import SpecChangeControl from "components/Config/AnalysisConfigurationPage/SpecChangeControl"
import { message, limsSpecs, limsSpecVersions } from "state_management"

// ONE analysis's parameter specification (SPEC-063, Plannertech-user#4149) — opened from the
// Analysis-specifications register.
//
// It reuses `components/Lims/SpecEditor` UNCHANGED. The editor was never the problem; its location
// was. A copy would have drifted from the original within a release (the lesson of RR8
// #4063/#4068), and the calculated-parameter engine, the reference validation and the preview all
// hang off it.
//
// Two behaviours matter here:
//
//  * **The author CHOOSES where a change starts from** (SPEC-070, Plannertech-user#4172). A version
//    is never edited once saved: every save with changed content becomes a NEW version, built on the
//    version the editor started from. The default is the version IN FORCE. When a version is still
//    awaiting approval, the author may instead continue from the newest one (the #4092 lesson: an
//    author reopening their own pending change must be able to build on it rather than seeing it as
//    if it had been thrown away). The choice is sent as `basedOnVersionId`, and the server approves
//    a version only while what it was built on is in force — so a change built on a draft waits for
//    that draft. It used to seed from the pending draft silently, which put a colleague's unapproved
//    lines into a new version under another author's name. The seed still WAITS for the version
//    fetch, so it never overwrites whatever the user has already typed.
//  * **A change to an APPROVED specification demands a stated reason**, asked in a popup at save
//    time (the #4073/#4074 shape), audited server-side. The FIRST specification on an analysis that
//    has none is its approved baseline, not a change, and needs no reason.
//
// `canAuthor` false (any LIMS user who is not the planner) renders the same screen read-only: the
// save bar is gone and the body is `inert`, so a reviewer can read what they are about to sign
// without being able to type into it. The server refuses the write regardless.
export default function AnalysisSpecificationEditor({ analysisDefinitionId, canAuthor, onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const row = useSelector((state) => limsSpecs.selectSpecRow(state, analysisDefinitionId))
  const members = useSelector((state) => state.members)

  const [parameters, setParameters] = useState([])
  const [specValid, setSpecValid] = useState(true)
  const [reasonPrompt, setReasonPrompt] = useState(false)
  const [changeReason, setChangeReason] = useState("")
  const [seeded, setSeeded] = useState(false)
  // SPEC-070: where this change starts from — the version in force (default) or the newest draft.
  const [startFrom, setStartFrom] = useState(START_IN_FORCE)
  const [versions, setVersions] = useState([])

  // The spec-level `formula` is a legacy field: nothing edits it any more, but it is part of the
  // versioned content, so it is carried through the save untouched rather than silently cleared.
  const formulaRef = useRef("")
  // What the editor was seeded WITH, so "did this actually change?" is judged against the same
  // source it was filled from — reopening an analysis with a pending draft must not read as a
  // fresh change (which would demand a reason and create a second draft on save). State, not a
  // ref: the starting-point choice reads it while rendering (SPEC-070).
  const [initialSnapshot, setInitialSnapshot] = useState(null)

  // Fill the editor from the chosen starting point. The version in force is the register row (the
  // effective projection, i.e. what result entry judges against — empty while nothing is approved).
  const seedFrom = (choice, items) => {
    const draft = choice === START_DRAFT ? limsSpecVersions.pendingVersionOf(items) : null
    const source = draft || row || {}
    const seedParameters = limsSpecs.normalizeParameters(source.parameters)
    formulaRef.current = source.formula || ""
    setInitialSnapshot(JSON.stringify(seedParameters))
    setParameters(seedParameters)
  }

  useEffect(() => {
    let cancelled = false
    const seed = (items) => {
      if (cancelled) return
      setVersions(items || [])
      seedFrom(START_IN_FORCE, items)
      setSeeded(true)
    }
    dispatch(limsSpecVersions.fetch(analysisDefinitionId)).then(
      (items) => seed(items),
      // A failed version fetch must fall back to the effective spec, never leave the editor blank.
      () => seed([])
    )
    return () => {
      cancelled = true
    }
  }, [dispatch, analysisDefinitionId]) // eslint-disable-line react-hooks/exhaustive-deps

  const latestDraft = limsSpecVersions.pendingVersionOf(versions)
  const inForce = limsSpecVersions.effectiveVersionOf(versions)

  const chooseStart = (choice) => {
    setStartFrom(choice)
    seedFrom(choice, versions)
  }

  const changed = () =>
    initialSnapshot !== null && JSON.stringify(limsSpecs.normalizeParameters(parameters)) !== initialSnapshot

  // A reason is required only when an APPROVED specification is being changed. `row` carries the
  // effective version number, which is exactly "there is something approved to supersede".
  const needsReason = () => Boolean(row && row.effectiveVersionNumber && changed())

  const persist = (reason) => {
    // SPEC-070: always name the base the editor was SEEDED from, the version in force included.
    // Leaving it out lets the server default to whatever is in force AT SAVE TIME — so if another
    // version was approved while this editor was open, the change (made on the old content) would
    // be stamped as built on the new one and, once approved, silently undo it. Named explicitly,
    // a base that has since been superseded is refused (BASE_VERSION_INVALID) instead.
    const seededFrom = startFrom === START_DRAFT && latestDraft ? latestDraft : inForce
    const basedOnVersionId = seededFrom ? seededFrom.id : null
    dispatch(limsSpecs.save(analysisDefinitionId, { parameters, formula: formulaRef.current }, reason, basedOnVersionId))
      .then(() => {
        // Every save with changed content is a new DRAFT (SPEC-068: the first specification too —
        // the old "saved and now in force" message for a first save was untrue). SPEC-070: say
        // what the new version was built on.
        dispatch(
          message.success(
            seededFrom
              ? t("limsAnalysisSpecSavedDraftOn", { n: seededFrom.versionNumber })
              : t("limsAnalysisSpecSavedDraft")
          )
        )
        // Refresh the register so its status column reflects the save immediately, then step back
        // to it: "awaiting review" on the row it came from is the clearest possible answer to
        // "where did my change go?".
        dispatch(limsSpecs.fetch())
        onClose()
      })
      .catch((err) => {
        // A coded refusal (SPEC-070: BASE_VERSION_INVALID when the base was replaced while the
        // editor was open) is already shown, translated, by the message layer; the generic
        // "could not be saved" would overwrite that reason with a sentence that explains nothing.
        if (err && err.code && i18n.exists(`code.${err.code}`, { ns: "messages" })) return
        dispatch(message.error(t("limsAnalysisSpecSaveFailed")))
      })
  }

  const handleSave = () => {
    if (!specValid) {
      dispatch(message.warning(t("limsCalcFormulaInvalidSave")))
      return
    }
    if (!changed()) {
      onClose()
      return
    }
    if (needsReason()) {
      setReasonPrompt(true)
      return
    }
    persist(null)
  }

  if (!seeded) {
    return (
      <Popup onCancel={onClose}>
        <div className="LimsAnalysisSpecEditor">
          <Loading />
        </div>
      </Popup>
    )
  }

  return (
    <>
      <Popup onCancel={onClose}>
        {/* Deliberately NOT `nonScrollable`: a long parameter list plus the version history is
            taller than the viewport, and the popup is what scrolls it. */}
        <div className="LimsAnalysisSpecEditor">
          {!canAuthor && (
            <p className="LimsAnalysisSpecEditor-readonly">
              <FiInfo /> <span>{t("limsAnalysisSpecReadOnly")}</span>
            </p>
          )}

          {/* The modal says WHICH analysis it is editing — that is what the page heading used to
              carry, and a modal over a register has to name its row. */}
          <ConfigContainer
            title={(row && row.name) || t("limsSpecification")}
            disabled={!canAuthor}
            cancelHandler={canAuthor ? onClose : undefined}
            submitHandler={canAuthor ? handleSave : undefined}
          >
            {/* SPEC-070: the starting point, a dropdown defaulting to the version in force, offered only
                when there is a draft to continue from. It locks once the author has edited, so
                switching can never silently discard changes. */}
            {canAuthor && latestDraft && (
              <div className="LimsAnalysisSpecEditor-start">
                <Container marginBottom="">
                  <Title>{t("limsSpecStartFrom")}</Title>
                  <Select
                    isMulti={false}
                    collection={[
                      {
                        id: START_IN_FORCE,
                        name: inForce
                          ? t("limsSpecStartFromInForce", { n: inForce.versionNumber })
                          : t("limsSpecStartFromEmpty"),
                      },
                      {
                        id: START_DRAFT,
                        name: t("limsSpecStartFromDraft", {
                          n: latestDraft.versionNumber,
                          initials: (members[latestDraft.createdBy] && members[latestDraft.createdBy].initials) || "—",
                        }),
                      },
                    ]}
                    value={startFrom}
                    onChange={(v) => chooseStart(v[0] || START_IN_FORCE)}
                    menuPlacement="bottom"
                    disabled={changed()}
                  />
                </Container>
                <p className="LimsAnalysisSpecEditor-start-help">{t(startHelpKey(changed(), startFrom))}</p>
              </div>
            )}
            <div className="LimsAnalysisSpecEditor-body" inert={!canAuthor ? "" : undefined}>
              <SpecEditor
                parameters={parameters}
                onParametersChange={setParameters}
                onValidityChange={setSpecValid}
              />
            </div>
          </ConfigContainer>

          <hr />
          <p className="Pheading ConfigurationPage">{t("limsSpecVersions")}</p>
          <div className="ConfigSection-body">
            <SpecChangeControl analysisDefinitionId={analysisDefinitionId} />
          </div>
        </div>
      </Popup>

      {/* The reason for changing an approved specification, asked next to the action that needs it.
          Mandatory — the save is audited against it server-side, so there is no "save anyway". */}
      {reasonPrompt && (
        <Popup nonScrollable onCancel={() => setReasonPrompt(false)}>
          <ConfigContainer
            title={t("limsSpecChangeReason")}
            cancelHandler={() => setReasonPrompt(false)}
            submitHandler={() => {
              if (!changeReason.trim()) {
                dispatch(message.warning(t("limsSpecChangeReasonRequired")))
                return
              }
              setReasonPrompt(false)
              persist(changeReason)
            }}
          >
            <Container>
              <Title>{t("limsSpecChangeReasonHelp")}</Title>
              <Text value={changeReason} onChange={setChangeReason} />
            </Container>
          </ConfigContainer>
        </Popup>
      )}
    </>
  )
}

// SPEC-070 — the two starting points a change can be built on.
const START_IN_FORCE = "inForce"
const START_DRAFT = "draft"

// The line under the starting-point choice: why it is locked, or what the chosen start means.
function startHelpKey(isChanged, startFrom) {
  if (isChanged) return "limsSpecStartFromLocked"
  return startFrom === START_DRAFT ? "limsSpecStartFromDraftHelp" : "limsSpecStartFromInForceHelp"
}

